// 通用对话框系统 —— 取代浏览器原生 alert / confirm / prompt
//
// 用法:
//   const dlg = useDialog();
//   if (await dlg.confirm({ title: "撤销?", message: "..." })) { ... }
//   await dlg.alert({ message: "失败" });
//   const v = await dlg.prompt({ label: "新链接", defaultValue: "..." });
//
// 设计:
//   - 单 Provider 顶层挂；所有 dialog 都是 imperative API (Promise) 返回
//   - 视觉沿用 .modal-backdrop / .modal 同一套黑白专业风
//   - Esc / 点 backdrop 关闭 = 选取消 (resolve false / null)

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// ---------- 类型 ----------

type Variant = "default" | "danger";

interface ConfirmOpts {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
}

interface AlertOpts {
  title?: string;
  message: string;
  okLabel?: string;
}

interface PromptOpts {
  title?: string;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 是否可读不可改（只为复制场景） */
  readOnly?: boolean;
}

type DialogState =
  | { kind: "none" }
  | {
      kind: "confirm";
      opts: ConfirmOpts;
      resolve: (v: boolean) => void;
    }
  | {
      kind: "alert";
      opts: AlertOpts;
      resolve: () => void;
    }
  | {
      kind: "prompt";
      opts: PromptOpts;
      resolve: (v: string | null) => void;
    };

interface DialogApi {
  confirm(opts: ConfirmOpts): Promise<boolean>;
  alert(opts: AlertOpts): Promise<void>;
  prompt(opts: PromptOpts): Promise<string | null>;
}

const DialogContext = createContext<DialogApi | null>(null);

// ---------- Provider ----------

export function DialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>({ kind: "none" });
  const [promptValue, setPromptValue] = useState("");

  const api = useMemo<DialogApi>(
    () => ({
      confirm(opts) {
        return new Promise<boolean>((resolve) => {
          setState({ kind: "confirm", opts, resolve });
        });
      },
      alert(opts) {
        return new Promise<void>((resolve) => {
          setState({ kind: "alert", opts, resolve });
        });
      },
      prompt(opts) {
        setPromptValue(opts.defaultValue ?? "");
        return new Promise<string | null>((resolve) => {
          setState({ kind: "prompt", opts, resolve });
        });
      },
    }),
    [],
  );

  const close = useCallback(() => setState({ kind: "none" }), []);

  // Esc 关闭 = 取消
  useEffect(() => {
    if (state.kind === "none") return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (state.kind === "confirm") state.resolve(false);
        else if (state.kind === "alert") state.resolve();
        else if (state.kind === "prompt") state.resolve(null);
        close();
      }
      if (e.key === "Enter" && state.kind === "alert") {
        state.resolve();
        close();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  return (
    <DialogContext.Provider value={api}>
      {children}
      {state.kind !== "none" && (
        <div
          className="modal-backdrop"
          onClick={() => {
            if (state.kind === "confirm") state.resolve(false);
            else if (state.kind === "alert") state.resolve();
            else if (state.kind === "prompt") state.resolve(null);
            close();
          }}
        >
          <div className="modal dialog" onClick={(e) => e.stopPropagation()}>
            {state.kind === "confirm" && (
              <>
                {state.opts.title && (
                  <header className="modal__header">
                    <h2>{state.opts.title}</h2>
                  </header>
                )}
                <div className="modal__body">
                  <p className="dialog__msg">{state.opts.message}</p>
                  <div className="dialog__actions">
                    <button
                      className="btn btn--ghost"
                      onClick={() => {
                        state.resolve(false);
                        close();
                      }}
                    >
                      {state.opts.cancelLabel ?? "取消"}
                    </button>
                    <button
                      className={
                        state.opts.variant === "danger"
                          ? "btn btn--danger"
                          : "btn btn--primary"
                      }
                      onClick={() => {
                        state.resolve(true);
                        close();
                      }}
                    >
                      {state.opts.confirmLabel ?? "确定"}
                    </button>
                  </div>
                </div>
              </>
            )}
            {state.kind === "alert" && (
              <>
                {state.opts.title && (
                  <header className="modal__header">
                    <h2>{state.opts.title}</h2>
                  </header>
                )}
                <div className="modal__body">
                  <p className="dialog__msg">{state.opts.message}</p>
                  <div className="dialog__actions">
                    <button
                      className="btn btn--primary"
                      autoFocus
                      onClick={() => {
                        state.resolve();
                        close();
                      }}
                    >
                      {state.opts.okLabel ?? "知道了"}
                    </button>
                  </div>
                </div>
              </>
            )}
            {state.kind === "prompt" && (
              <>
                {state.opts.title && (
                  <header className="modal__header">
                    <h2>{state.opts.title}</h2>
                  </header>
                )}
                <div className="modal__body">
                  {state.opts.label && (
                    <label className="dialog__label">{state.opts.label}</label>
                  )}
                  <input
                    className="input"
                    autoFocus={!state.opts.readOnly}
                    value={promptValue}
                    onChange={(e) => setPromptValue(e.target.value)}
                    placeholder={state.opts.placeholder}
                    readOnly={state.opts.readOnly}
                    onFocus={(e) => state.opts.readOnly && e.currentTarget.select()}
                  />
                  <div className="dialog__actions">
                    <button
                      className="btn btn--ghost"
                      onClick={() => {
                        state.resolve(null);
                        close();
                      }}
                    >
                      {state.opts.cancelLabel ?? "取消"}
                    </button>
                    <button
                      className="btn btn--primary"
                      onClick={() => {
                        state.resolve(promptValue);
                        close();
                      }}
                    >
                      {state.opts.confirmLabel ?? "确定"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogApi {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be inside <DialogProvider>");
  return ctx;
}
